/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
/* eslint-disable max-len */
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  getAllFeatureDefinitions,
  createFeatureDefinition,
  updateFeatureDefinition,
  deleteFeatureDefinition,
} from '../../utils/api';

function FeatureDefinitionsManagement() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(null);
  const [formData, setFormData] = useState({
    feature_key: '',
    name: '',
    description: '',
    category: '',
    default_enabled: false,
    is_active: true,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Prédefined features based on your list
  const predefinedFeatures = [
    {
      key: 'regulation', name: 'Réglementation', category: 'Core', description: 'Gestion de la réglementation transport sanitaire'
    },
    {
      key: 'facturation', name: 'Facturation', category: 'Finance', description: 'Module de facturation'
    },
    {
      key: 'plannings', name: 'Plannings', category: 'Opérations', description: 'Gestion des plannings'
    },
    {
      key: 'equipages', name: 'Équipages', category: 'Personnel', description: 'Gestion des équipages'
    },
    {
      key: 'pointeuse', name: 'Pointeuse', category: 'Personnel', description: 'Système de pointage'
    },
    {
      key: 'parc-automobile', name: 'Parc Automobile', category: 'Véhicules', description: 'Gestion du parc de véhicules'
    },
    {
      key: 'personnel', name: 'Personnel', category: 'Personnel', description: 'Gestion du personnel'
    },
    {
      key: 'etablissements', name: 'Établissements', category: 'Core', description: 'Gestion des établissements'
    },
    {
      key: 'support', name: 'Support', category: 'Support', description: 'Support client'
    },
    {
      key: 'geolocalisation', name: 'Géolocalisation', category: 'Tracking', description: 'Géolocalisation des véhicules'
    },
    {
      key: 'application', name: 'Application Mobile', category: 'Mobile', description: 'Application mobile'
    },
    {
      key: 'stock', name: 'Gestion de Stock', category: 'Inventaire', description: 'Gestion des stocks'
    },
    {
      key: 'materiel-medical', name: 'Matériel Médical', category: 'Médical', description: 'Gestion du matériel médical'
    },
    {
      key: 'documents', name: 'Documents', category: 'Documents', description: 'Gestion documentaire'
    },
    {
      key: 'multi-societe', name: 'Multi-Société', category: 'Enterprise', description: 'Gestion multi-société'
    },
    {
      key: 'tableau-de-bord', name: 'Tableau de Bord', category: 'Analytics', description: 'Tableaux de bord personnalisés'
    },
    {
      key: 'donnees-exploitation', name: 'Données d\'Exploitation', category: 'Analytics', description: 'Analyse des données d\'exploitation'
    },
    {
      key: 'processus', name: 'Processus', category: 'Workflow', description: 'Gestion des processus métier'
    },
    {
      key: 'taches', name: 'Tâches', category: 'Workflow', description: 'Gestion des tâches'
    },
  ];

  const categories = ['Core', 'Finance', 'Opérations', 'Personnel', 'Véhicules', 'Support', 'Tracking', 'Mobile', 'Inventaire', 'Médical', 'Documents', 'Enterprise', 'Analytics', 'Workflow'];

  const fetchData = async () => {
    setLoading(true);
    try {
      const featuresData = await getAllFeatureDefinitions();
      setFeatures(featuresData);
    }
    catch (err) {
      console.error('Erreur lors du chargement des fonctionnalités:', err);
      setError(err.message || 'Une erreur est survenue lors du chargement des données');
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      feature_key: '',
      name: '',
      description: '',
      category: '',
      default_enabled: false,
      is_active: true,
    });
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (feature) => {
    setCurrentFeature(feature);
    setFormData({
      feature_key: feature.feature_key,
      name: feature.name,
      description: feature.description || '',
      category: feature.category || '',
      default_enabled: feature.default_enabled || false,
      is_active: feature.is_active,
    });
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (feature) => {
    setCurrentFeature(feature);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const {
      name, value, type, checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCreateFeature = async () => {
    try {
      await createFeatureDefinition(formData);
      handleCloseDialogs();
      fetchData();
      setSnackbar({
        open: true,
        message: 'Fonctionnalité créée avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la création de la fonctionnalité:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la création de la fonctionnalité',
        severity: 'error',
      });
    }
  };

  const handleUpdateFeature = async () => {
    try {
      await updateFeatureDefinition(currentFeature.feature_key, formData);
      handleCloseDialogs();
      fetchData();
      setSnackbar({
        open: true,
        message: 'Fonctionnalité mise à jour avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la mise à jour de la fonctionnalité:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la mise à jour de la fonctionnalité',
        severity: 'error',
      });
    }
  };

  const handleDeleteFeature = async () => {
    try {
      await deleteFeatureDefinition(currentFeature.feature_key);
      handleCloseDialogs();
      fetchData();
      setSnackbar({
        open: true,
        message: 'Fonctionnalité supprimée avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la suppression de la fonctionnalité:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la suppression de la fonctionnalité',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCreatePredefinedFeatures = async () => {
    setLoading(true);
    try {
      for (const feature of predefinedFeatures) {
        const existingFeature = features.find((f) => f.feature_key === feature.key);
        if (!existingFeature) {
          await createFeatureDefinition({
            feature_key: feature.key,
            name: feature.name,
            description: feature.description,
            category: feature.category,
            default_enabled: false,
            is_active: true,
          });
        }
      }
      fetchData();
      setSnackbar({
        open: true,
        message: 'Fonctionnalités prédéfinies créées avec succès',
        severity: 'success',
      });
    }
    catch (err) {
      console.error('Erreur lors de la création des fonctionnalités prédéfinies:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Une erreur est survenue lors de la création des fonctionnalités prédéfinies',
        severity: 'error',
      });
    }
    finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3
      }}
      >
        <Typography variant="h4">
          Gestion des Définitions de Fonctionnalités
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={handleCreatePredefinedFeatures}
            sx={{ mr: 2 }}
          >
            Créer Fonctionnalités Prédéfinies
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Ajouter une Fonctionnalité
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="table des fonctionnalités">
            <TableHead>
              <TableRow>
                <TableCell>Clé</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell>Par Défaut</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {features
                .slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage)
                .map((feature) => (
                  <TableRow
                    key={feature.feature_key}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <code>{feature.feature_key}</code>
                    </TableCell>
                    <TableCell>{feature.name}</TableCell>
                    <TableCell>{feature.description || '-'}</TableCell>
                    <TableCell>
                      {feature.category && (
                        <Chip
                          label={feature.category}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={feature.default_enabled ? 'Oui' : 'Non'}
                        color={feature.default_enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={feature.is_active ? 'Actif' : 'Inactif'}
                        color={feature.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="info"
                        onClick={() => handleOpenEditDialog(feature)}
                        title="Modifier"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleOpenDeleteDialog(feature)}
                        title="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              {features.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Aucune fonctionnalité trouvée
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={features.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      {/* Create Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter une nouvelle fonctionnalité</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="feature_key"
                label="Clé de la fonctionnalité"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.feature_key}
                onChange={handleInputChange}
                required
                helperText="Identifiant unique (ex: facturation, planning)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Nom"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                name="category"
                label="Catégorie"
                fullWidth
                variant="outlined"
                value={formData.category}
                onChange={handleInputChange}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={formData.default_enabled}
                    onChange={handleInputChange}
                    name="default_enabled"
                  />
                )}
                label="Activée par défaut"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    name="is_active"
                  />
                )}
                label="Fonctionnalité active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Annuler</Button>
          <Button onClick={handleCreateFeature} variant="contained">Créer</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier la fonctionnalité</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="feature_key"
                label="Clé de la fonctionnalité"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.feature_key}
                disabled
                helperText="La clé ne peut pas être modifiée"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="name"
                label="Nom"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                name="category"
                label="Catégorie"
                fullWidth
                variant="outlined"
                value={formData.category}
                onChange={handleInputChange}
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={formData.default_enabled}
                    onChange={handleInputChange}
                    name="default_enabled"
                  />
                )}
                label="Activée par défaut"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    name="is_active"
                  />
                )}
                label="Fonctionnalité active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Annuler</Button>
          <Button onClick={handleUpdateFeature} variant="contained">Mettre à jour</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Supprimer la fonctionnalité</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer la fonctionnalité <strong>{currentFeature?.name}</strong> ?
            Cette action est irréversible et affectera tous les tenants qui utilisent cette fonctionnalité.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Annuler</Button>
          <Button onClick={handleDeleteFeature} color="error" variant="contained">Supprimer</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FeatureDefinitionsManagement;
